/* eslint-disable react/no-this-in-sfc */

import { tScatterDiagramData } from "types/sharedTypes"

import { useMemo } from "react"

import getAmountMenWomen from "utils/Highcharts/getAmountMenWomen"
import getSsnAndNameFromChartData from "utils/Highcharts/getSsnAndNameFromChartData"

import Highcharts from "highcharts"
import Exporting from "highcharts/modules/exporting"
import HighchartsReact from "highcharts-react-official"
import HighchartsMore from "highcharts/highcharts-more"

import defaultSetOptions from "components/cores/highcharts/highchartsDefaultSetOptions"

HighchartsMore(Highcharts)
Exporting(Highcharts)
Highcharts.setOptions(defaultSetOptions)

type tScatterDiagramProps = {
    diagramData: tScatterDiagramData[]
    diagramTitle: string
    showMinorGridLines: boolean
    showGender: boolean
    diagramGrouping: { Key: number; Value: string }[]
    selectedGrouping: string
}

const emptyDiagramData = {
    SalariesMen: [],
    SalariesWomen: [],
    Catagories: [],
}

const ScatterDiagram = ({
    diagramData,
    diagramTitle,
    showMinorGridLines,
    showGender,
    diagramGrouping,
    selectedGrouping,
}: tScatterDiagramProps) => {
    const selectedDiagramGrouping = diagramGrouping.find((grouping) => grouping.Value === selectedGrouping)

    const currentData =
        selectedDiagramGrouping !== undefined && diagramData?.[selectedDiagramGrouping.Key]
            ? diagramData[selectedDiagramGrouping.Key]
            : emptyDiagramData

    const hasValidData =
        Array.isArray(currentData.SalariesMen) &&
        Array.isArray(currentData.SalariesWomen) &&
        Array.isArray(currentData.Catagories)

    const amounts = useMemo(() => getAmountMenWomen(currentData), [currentData])

    const chartOptions = useMemo<Highcharts.Options>(
        () => ({
            chart: {
                animation: false,
                type: "scatter",
            },
            title: {
                text: diagramTitle,
            },
            yAxis: {
                title: {
                    text: "Lön",
                },
                labels: {
                    style: {
                        fontSize: "14px",
                    },
                },
                minorTickInterval: showMinorGridLines ? "auto" : undefined,
            },
            xAxis: {
                categories: currentData.Catagories,
                title: {
                    text: selectedGrouping,
                },
                labels: {
                    style: {
                        fontSize: "14px",
                    },
                },
            },
            tooltip: {
                formatter(this: Highcharts.TooltipFormatterContextObject) {
                    const x = this.x as number
                    const y = this.y as number

                    const seriesDef = [{ data: currentData.SalariesMen }, { data: currentData.SalariesWomen }]

                    const { ssn, name } = getSsnAndNameFromChartData(seriesDef, x, y, currentData.Catagories)

                    return `PNR: ${ssn}<br> Namn: ${name} <br><br> Lön: ${this.y} kr`
                },
            },
            series: [
                {
                    id: "1",
                    type: "scatter",
                    name: `Man ${amounts.Men}%`,
                    data: currentData.SalariesMen,
                    marker: showGender
                        ? { symbol: undefined, fillColor: undefined }
                        : { symbol: "circle", fillColor: "#000" },
                },
                {
                    id: "2",
                    type: "scatter",
                    name: `Kvinna ${amounts.Women}%`,
                    data: currentData.SalariesWomen,
                    marker: showGender
                        ? { symbol: undefined, fillColor: "#BA8C0D" }
                        : { symbol: "circle", fillColor: "#000" },
                },
            ],
            credits: {
                enabled: false,
            },
        }),
        [amounts.Men, amounts.Women, currentData, diagramTitle, selectedGrouping, showGender, showMinorGridLines]
    )

    if (!hasValidData) {
        return <div>Ingen data tillgänglig</div>
    }

    return (
        <div>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} immutable />
        </div>
    )
}

export default ScatterDiagram
